import React from "react";
import { Play, Pause, Heart, MoreVertical } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePlayer } from "@/context/PlayerContext";
import { coverFor, Waveform } from "@/components/common";

export function SongCard({ song, list, size = "md" }) {
  const { current, playing, playSong, toggle } = usePlayer();
  const navigate = useNavigate();
  const isCurrent = current?.id === song.id;
  const w = size === "sm" ? "w-36" : "w-44 sm:w-48";

  return (
    <div className={`jx-card rounded-2xl p-3 ${w} shrink-0 group`} data-testid={`song-card-${song.id}`}>
      <div className="relative rounded-xl overflow-hidden aspect-square mb-3">
        <img src={coverFor(song)} alt={song.title} className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <button
          data-testid={`play-song-${song.id}`}
          onClick={(e) => { e.stopPropagation(); isCurrent ? toggle() : playSong(song, list); }}
          className="absolute bottom-2 right-2 w-11 h-11 rounded-full jx-cta grid place-items-center text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity translate-y-1 group-hover:translate-y-0"
        >
          {isCurrent && playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
        </button>
        {isCurrent && playing && (
          <div className="absolute bottom-2 left-2"><Waveform bars={4} /></div>
        )}
      </div>
      <div className="cursor-pointer" onClick={() => navigate(`/song/${song.id}`)}>
        <p className="font-semibold text-sm truncate text-slate-100">{song.title}</p>
        <p className="text-xs text-slate-400 truncate mt-0.5">{song.creator_name || "Unknown"}</p>
      </div>
      <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
        <span className="flex items-center gap-1"><Play size={11} /> {song.plays || 0}</span>
        <span className="flex items-center gap-1"><Heart size={11} /> {song.likes || 0}</span>
        {song.genre && <span className="ml-auto font-mono uppercase text-[9px] text-purple-400 tracking-wider">{song.genre}</span>}
      </div>
    </div>
  );
}

export function SongRow({ song, list, index }) {
  const { current, playing, playSong, toggle } = usePlayer();
  const navigate = useNavigate();
  const isCurrent = current?.id === song.id;
  return (
    <div
      data-testid={`song-row-${song.id}`}
      className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/5 transition-colors group cursor-pointer"
      onClick={() => (isCurrent ? toggle() : playSong(song, list))}
    >
      <div className="relative w-12 h-12 rounded-lg overflow-hidden shrink-0">
        <img src={coverFor(song)} className="w-full h-full object-cover" alt="" />
        <div className="absolute inset-0 bg-black/40 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
          {isCurrent && playing ? <Pause size={16} /> : <Play size={16} />}
        </div>
      </div>
      <div className="min-w-0 flex-1" onClick={(e) => { e.stopPropagation(); navigate(`/song/${song.id}`); }}>
        <p className={`text-sm font-medium truncate ${isCurrent ? "text-purple-400" : "text-slate-100"}`}>{song.title}</p>
        <p className="text-xs text-slate-400 truncate">{song.creator_name}</p>
      </div>
      <span className="text-xs text-slate-500 font-mono">{song.plays || 0} plays</span>
    </div>
  );
}
